import { useState, useEffect } from 'react';
import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    FormHelperText,
    CircularProgress,
    Typography,
    TextField,
    Stack,
    Button,
    CardContent,
    Card,
    Collapse,
    Divider, Autocomplete
} from '@mui/material';
import {CheckCircleOutlineTwoTone, Warning, WarningAmber} from '@mui/icons-material';
import { CalendarIcon } from '@mui/x-date-pickers';
import {DiversityIcon} from "./Icons";
import {Controller, useForm} from "react-hook-form";

function CompanyFields({
                           empresa,
                           numEmpresas,
                           GHEs,
                           register,
                           setValue,
                           getValues,
                           errors,
                           clearErrors,
                           replicateVidaUtil,
                           unidadesDeMedida,
                           control,
}) {
    const [isChecked, setIsChecked] = useState(!!getValues(`id_empresas.${empresa.id}`));
    const [isCheckedPermission, setIsCheckedPermission] = useState(!!getValues(`isCheckedPermission.${empresa.id}`));

    const GHEsEmpresa = GHEs.filter((ghe) => ghe.id_empresa === empresa.id);

    const contratoAtivo = unidadesDeMedida.some(
        (u) => u.contrato.id === empresa.idContrato && u.isContractActive
    );

    function handleCheck(e) {
        const isChecked = !contratoAtivo ? false : e.target.checked;
        const value = isChecked ? empresa.id : false;
        setValue(`id_empresas.${empresa.id}`, value);
        setIsChecked(isChecked);

        if (!isChecked) {
            setValue(`vida_util.${empresa.id}`, '');
            GHEsEmpresa.forEach((ghe) => {
                setValue(`gruposSelecionadosCriarPermissao[${ghe.id}][status]`, false);
                setValue(`gruposSelecionadosCriarPermissao[${ghe.id}][nome]`, ghe.nome);
            });
        }
    }

    useEffect(() => {
        setIsChecked(!!getValues(`id_empresas.${empresa.id}`));
    }, [getValues, empresa.id]);

    return (
        <FormGroup>
            <Stack spacing={1} direction="row" alignItems="start" justifyContent="space-between" my={1} width={'100%'} flexWrap={'wrap'}>
                <Card variant='elevation' sx={{ width: '100%', paddingBottom: 0, opacity: contratoAtivo ? 1 : 0.6 }}>
                    <CardContent
                        sx={{
                            paddingX: 1,
                            paddingY: isChecked ? 1 : 0,
                            paddingBottom: 0,
                            "&:last-child": {
                                paddingBottom: isChecked ? 3 : 0,
                            }
                        }}
                    >
                        <Box sx={{ width: '100%', padding: 1, borderRadius: 1 }}>
                            <FormControlLabel
                                key={`check${empresa.id}`}
                                label={
                                    <Stack>
                                        <Typography>{empresa.nomeFantasia}</Typography>
                                        {!contratoAtivo && (
                                            <Stack display="flex" justifyContent="center" alignItems="center" direction="row">
                                                <Warning color="error" />
                                                <Typography color="error" variant="body2" alignItems='center'>
                                                    Contrato inativo — este produto não pode ser configurado para esta empresa.
                                                </Typography>
                                            </Stack>
                                        )}
                                    </Stack>
                                }
                                control={
                                    <Checkbox
                                        checked={isChecked}
                                        {...register(`id_empresas.${empresa.id}`)}
                                        onChange={handleCheck}
                                        disabled={!contratoAtivo}
                                    />
                                }
                                sx={{ width: '100%' }}
                            />
                        </Box>

                        <Collapse in={isChecked && contratoAtivo} timeout="auto" unmountOnExit>
                            <Divider sx={{ mb: 2 }} />

                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                alignItems="stretch"
                                gap={2}
                                width="100%"
                                flexWrap="wrap"
                                sx={{
                                    '& > .MuiCard-root': {
                                        flexGrow: 1,
                                        minWidth: { xs: '100%', sm: 320 },
                                        flexBasis: { xs: '100%', sm: 'calc(33.33% - 16px)' },
                                    },
                                }}
                            >
                                {/* Card 1 */}
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Stack direction="row" alignItems="center">
                                                <WarningAmber sx={{ mr: 1 }} color={isChecked ? 'warning' : 'disabled'} />
                                                Grupos da empresa relacionados à Categoria selecionada
                                            </Stack>
                                        </Typography>

                                        <Typography variant="body2" color="textSecondary" mt={2}>
                                            Ao cadastrar este produto, ele será automaticamente <b>designado para todos os grupos</b> que possuem a mesma
                                            categoria. No entanto, <b>ele permanecerá inativo</b> até que o responsável por cada grupo o ative manualmente.
                                        </Typography>

                                        <Typography variant="body2" color="textSecondary" mt={2}>
                                            Essa ação facilita a distribuição automática, mantendo o controle individual de ativação em cada grupo.
                                        </Typography>

                                        <FormControlLabel
                                            sx={{ mt: 2 }}
                                            control={
                                                <Checkbox
                                                    checked={isCheckedPermission}
                                                    {...register(`isCheckedPermission.${empresa.id}`)}
                                                    onChange={(e) => {
                                                        const checked = e.target.checked;
                                                        setValue(`isCheckedPermission.${empresa.id}`, checked);
                                                        setIsCheckedPermission(checked);
                                                    }}
                                                    name={`isCheckedPermission.${empresa.id}`}
                                                    color={errors?.isCheckedPermission?.[empresa.id] ? 'error' : 'primary'}
                                                />
                                            }
                                            label={
                                                <Typography
                                                    variant="body2"
                                                    color={errors?.isCheckedPermission?.[empresa.id] ? 'error' : 'inherit'}
                                                >
                                                    Estou ciente de que o produto será designado automaticamente a todos os grupos da categoria e permanecerá
                                                    inativo até ativação manual.
                                                </Typography>
                                            }
                                        />

                                        {errors?.isCheckedPermission?.[empresa.id] && (
                                            <Typography variant="caption" color="error">
                                                {errors?.isCheckedPermission?.[empresa.id].message}
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>

                                {/* Card 2 */}
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Stack direction="row" alignItems="center">
                                                <CalendarIcon sx={{ mr: 1 }} color={isChecked ? 'info' : 'disabled'} />
                                                Digite a Vida Útil do EPI para a empresa
                                            </Stack>
                                        </Typography>

                                        <Typography variant="body2" color="textSecondary" mt={1} mb={3}>
                                            A vida útil do EPI é o tempo que o EPI pode ser utilizado antes de ser descartado.
                                        </Typography>

                                        <TextField
                                            label="Vida Útil do EPI"
                                            variant="outlined"
                                            size="small"
                                            {...register(`vida_util.${empresa.id}`, { required: isChecked })}
                                            onChange={(e) => {
                                                setValue(`vida_util.${empresa.id}`, e.target.value);
                                                clearErrors(`vida_util.${empresa.id}`);
                                            }}
                                            disabled={!isChecked || !contratoAtivo}
                                            type="number"
                                            InputProps={{
                                                endAdornment: <Typography color="textSecondary">dias</Typography>,
                                            }}
                                            error={errors[`vida_util`] && !!errors[`vida_util`][empresa.id]}
                                            helperText={errors[`vida_util`] && errors[`vida_util`][empresa.id]?.message}
                                            inputProps={{ min: 1 }}
                                            InputLabelProps={{
                                                shrink: !!getValues(`vida_util.${empresa.id}`) || isChecked,
                                            }}
                                            sx={{ display: 'block', mb: 1 }}
                                        />

                                        {numEmpresas > 1 && (
                                            <Button
                                                variant="text"
                                                size="small"
                                                onClick={() => replicateVidaUtil(getValues(`vida_util.${empresa.id}`))}
                                                startIcon={<CheckCircleOutlineTwoTone />}
                                                disabled={!getValues(`vida_util.${empresa.id}`)}
                                            >
                                                Replicar valor para todas empresas
                                            </Button>
                                        )}
                                    </CardContent>
                                </Card>

                                {/* Card 3 */}
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Stack direction="row" alignItems="center">
                                                <DiversityIcon sx={{ mr: 1 }} color={isChecked ? 'info' : 'disabled'} />
                                                Selecione a Unidade de Medida desse produto para a empresa
                                            </Stack>
                                        </Typography>

                                        <Controller
                                            name={`id_unidadeMedida.${empresa.id}`}
                                            control={control}
                                            rules={{ required: 'A Unidade de Medida é obrigatória.' }}
                                            {...register(`id_unidadeMedida.${empresa.id}`)}
                                            render={({ field }) => (
                                                <Autocomplete
                                                    {...field}
                                                    disabled={!contratoAtivo || !isChecked}
                                                    options={unidadesDeMedida.filter(u => u.contrato.id === empresa.idContrato)}
                                                    getOptionLabel={(option) => option.nome}
                                                    noOptionsText="Nenhuma Unidade encontrada"
                                                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                                                    value={unidadesDeMedida.find((option) => option.id === field.value) || null}
                                                    onChange={(event, value) => {
                                                        setValue(`id_unidadeMedida.${empresa.id}`, value ? value.id : null);
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label={`Unidade de Medida (${empresa.nomeFantasia})`}
                                                            required
                                                            autoComplete="off"
                                                            error={!!errors?.id_unidadeMedida?.[empresa.id]}
                                                            helperText={errors?.id_unidadeMedida?.[empresa.id]?.message}
                                                            variant="outlined"
                                                            InputLabelProps={{ shrink: true }}
                                                            placeholder="Selecione uma Unidade de Medida"
                                                        />
                                                    )}
                                                    sx={{ mt: 4 }}
                                                />
                                            )}
                                        />
                                    </CardContent>
                                </Card>
                            </Stack>
                        </Collapse>
                    </CardContent>
                </Card>
            </Stack>
        </FormGroup>
    )
}

function CompaniesCheckbox({
                               empresasAssociadas,
                               GHEs,
                               register,
                               setValue,
                               getValues,
                               errors,
                               clearErrors,
                               unidadesDeMedida,
                               control,
}) {

    const replicateVidaUtil = (vidaUtil) => {
        empresasAssociadas.forEach((empresa) => {
            const isChecked = getValues(`id_empresas.${empresa.id}`);
            if (isChecked) {
                setValue(`vida_util.${empresa.id}`, vidaUtil);
            }
        });
    };

    const numEmpresas = empresasAssociadas ? empresasAssociadas.length : 0;

    return (
        <Box sx={{ display: 'flex', width: '100%', flexDirection: 'column' }}>
            <FormControl sx={{ mx: 1 }} component="fieldset" variant="standard">
                <FormLabel component="legend" sx={{ mb: 1 }}>
                    Selecione as empresas as quais deverão ser relacionadas ao seu Produto:
                </FormLabel>
                <>
                    {empresasAssociadas ? empresasAssociadas.map((empresa) => (
                        <FormControl key={empresa.id}>
                            <CompanyFields
                                empresa={empresa}
                                numEmpresas={numEmpresas}
                                GHEs={GHEs}
                                register={register}
                                setValue={setValue}
                                getValues={getValues}
                                errors={errors}
                                clearErrors={clearErrors}
                                replicateVidaUtil={replicateVidaUtil}
                                unidadesDeMedida={unidadesDeMedida}
                                control={control}
                            />
                        </FormControl>
                    ))
                        : <Typography variant='body2' sx={{ mt: 2 }}><CircularProgress size={20} /> Carregando empresas...</Typography>
                    }
                </>
                <FormHelperText>Selecione ao menos uma empresa</FormHelperText>
            </FormControl>
        </Box>
    );
}

export default CompaniesCheckbox;
